import * as XLSX from 'xlsx';
import { format } from 'date-fns/format';
import type { SalesTadMarginRow } from '../types/primes';

/**
 * PRO-1364 — Export XLS de la marge mensuelle des Sales / TAD.
 *
 * Colonnes minimales de l'AC2 : identité du Bloomie, mois concerné, marge freelance, marge CDI et
 * marge totale. Le format exact (identifiants Lucca, colonnes supplémentaires) reste à finaliser
 * avec Jessica — cf. questions ouvertes du ticket.
 */
export const SALES_TAD_MARGINS_EXPORT_HEADERS = [
  'Prénom',
  'Nom',
  'Email',
  'Identifiant Lucca',
  'Mois',
  'Marge freelance (€)',
  'Marge CDI (€)',
  'Marge totale (€)',
] as const;

const roundEuro = (value: number): number => Math.round(value * 100) / 100;

/** Feuille au format tableau (matrice) : en-tête + une ligne par Bloomie sélectionné. */
export const buildSalesTadMarginsSheetRows = (
  rows: SalesTadMarginRow[],
  referenceDate: Date,
): (string | number)[][] => {
  const monthLabel = format(referenceDate, 'MM/yyyy');
  return [
    [...SALES_TAD_MARGINS_EXPORT_HEADERS],
    ...rows.map((row) => [
      row.firstname,
      row.lastname,
      row.email,
      row.luccaId ?? '',
      monthLabel,
      roundEuro(row.totalFreelanceMarge),
      roundEuro(row.totalBillingMarge),
      roundEuro(row.totalMarge),
    ]),
  ];
};

export const buildSalesTadMarginsExportFilename = (referenceDate: Date): string =>
  `marges-sales-tad-${format(referenceDate, 'yyyy-MM')}.xlsx`;

export type SalesTadMarginsExportResult = {
  rowsExported: number;
};

/** Génère et déclenche le téléchargement du fichier XLS des marges Sales / TAD. */
export const downloadSalesTadMarginsExport = (
  rows: SalesTadMarginRow[],
  referenceDate: Date,
): SalesTadMarginsExportResult => {
  const sheetRows = buildSalesTadMarginsSheetRows(rows, referenceDate);
  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Marges Sales-TAD');
  XLSX.writeFile(workbook, buildSalesTadMarginsExportFilename(referenceDate));
  return { rowsExported: rows.length };
};
